import React, { useState, useEffect } from 'react';
import { ArrowLeft, ShieldCheck, FileText } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

export default function LegalPage() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('terms');

  // Si viene con un hash en la URL (ej: /legal#privacy), abrimos esa pestaña
  useEffect(() => {
    if (location.hash === '#privacy') setActiveTab('privacy');
    else if (location.hash === '#terms') setActiveTab('terms');
  }, [location]);

  return (
    <div className="min-h-screen bg-[#030712] text-slate-300 selection:bg-blue-500/30">
      {/* Header simple */}
      <header className="border-b border-slate-800 bg-slate-950/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm font-medium">
            <ArrowLeft size={16} /> Volver a DinamiClass
          </Link>
          <img src="/logo.png" alt="DinamiClass" className="h-8 w-auto object-contain" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12 flex flex-col md:flex-row gap-12">
        {/* Sidebar de navegación */}
        <aside className="w-full md:w-64 shrink-0">
          <div className="sticky top-28 space-y-2">
            <button
              onClick={() => setActiveTab('terms')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'terms' ? 'bg-blue-600/10 text-blue-400 border border-blue-500/20' : 'text-slate-400 hover:bg-slate-900 hover:text-white border border-transparent'
              }`}
            >
              <FileText size={18} /> Términos de Servicio
            </button>
            <button
              onClick={() => setActiveTab('privacy')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'privacy' ? 'bg-blue-600/10 text-blue-400 border border-blue-500/20' : 'text-slate-400 hover:bg-slate-900 hover:text-white border border-transparent'
              }`}
            >
              <ShieldCheck size={18} /> Políticas de Privacidad
            </button>
          </div>
        </aside>

        {/* Contenido Legal */}
        <article className="flex-1 bg-slate-900 border border-slate-800 rounded-2xl p-8 md:p-12 prose prose-invert prose-blue max-w-none">
          {activeTab === 'terms' ? (
            <div className="animate-fade-in">
              <h1 className="text-3xl font-bold text-white mb-2">Términos y Condiciones de Servicio</h1>
              <p className="text-sm text-slate-500 mb-8">Última actualización: Abril de 2026</p>
              
              <div className="space-y-6 text-sm leading-relaxed text-slate-300">
                <p>Bienvenido a DinamiClass. Al acceder y utilizar nuestra plataforma (dinamiclassapp.com.ar), aceptas cumplir con los siguientes Términos y Condiciones. Si no estás de acuerdo con alguna parte de estos términos, no debes utilizar nuestro servicio.</p>

                <h3 className="text-lg font-semibold text-white mt-8 mb-3">1. Descripción del Servicio</h3>
                <p>DinamiClass es una plataforma SaaS de microlearning que utiliza Inteligencia Artificial para generar dinámicas de clase, guiones, resúmenes y evaluaciones para docentes. El servicio se ofrece a través de un sistema de créditos, donde cada generación consume una cantidad determinada de los mismos.</p>

                <h3 className="text-lg font-semibold text-white mt-8 mb-3">2. Registro y Seguridad de la Cuenta</h3>
                <p>Para utilizar nuestros servicios, debes registrarte proporcionando información veraz, precisa y actualizada. Eres responsable de mantener la confidencialidad de tus credenciales de acceso. DinamiClass no se hace responsable por pérdidas o daños derivados del acceso no autorizado a tu cuenta.</p>

                <h3 className="text-lg font-semibold text-white mt-8 mb-3">3. Sistema de Créditos y Suscripciones</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong>Planes Gratuitos:</strong> Otorgan créditos iniciales sin costo para probar la plataforma.</li>
                  <li><strong>Suscripciones (Estándar, Pro, Gold, Ultra):</strong> Se cobran de manera recurrente (mensual o anual) mediante Mercado Pago. Los créditos se renuevan cada 30 días.</li>
                  <li><strong>Cancelaciones:</strong> Puedes cancelar tu suscripción en cualquier momento desde la plataforma. Conservarás el acceso a tus funciones premium y créditos hasta que finalice el ciclo de facturación actual. No se realizan reembolsos por periodos parcialmente utilizados.</li>
                </ul>

                <h3 className="text-lg font-semibold text-white mt-8 mb-3">4. Uso de la Inteligencia Artificial</h3>
                <p>DinamiClass utiliza modelos de Inteligencia Artificial (LLMs) de terceros para procesar las solicitudes. Debido a la naturaleza probabilística de la IA, DinamiClass <strong>no garantiza la exactitud absoluta, idoneidad o veracidad</strong> del contenido generado. El docente es el único responsable de revisar, editar y validar el contenido antes de aplicarlo en el aula.</p>

                <h3 className="text-lg font-semibold text-white mt-8 mb-3">5. Propiedad Intelectual</h3>
                <p>La plataforma, su código, diseño y marca son propiedad exclusiva de DinamiClass. Sin embargo, <strong>tú conservas los derechos de uso comercial y no comercial sobre el material generado</strong> dentro de la plataforma utilizando tus créditos.</p>

                <h3 className="text-lg font-semibold text-white mt-8 mb-3">6. Modificaciones del Servicio</h3>
                <p>Nos reservamos el derecho de modificar o discontinuar el servicio, así como de actualizar estos términos en cualquier momento. Los cambios significativos en los precios o en las condiciones serán notificados al correo electrónico registrado.</p>
              </div>
            </div>
          ) : (
            <div className="animate-fade-in">
              <h1 className="text-3xl font-bold text-white mb-2">Políticas de Privacidad</h1>
              <p className="text-sm text-slate-500 mb-8">Última actualización: Abril de 2026</p>

              <div className="space-y-6 text-sm leading-relaxed text-slate-300">
                <p>En DinamiClass, nos tomamos muy en serio la privacidad de nuestros usuarios. Esta política describe cómo recopilamos, usamos y protegemos tu información personal de acuerdo con la <strong>Ley de Protección de Datos Personales N° 25.326 de la República Argentina</strong>.</p>

                <h3 className="text-lg font-semibold text-white mt-8 mb-3">1. Información que recopilamos</h3>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong>Datos de cuenta:</strong> Nombre, dirección de correo electrónico y contraseñas (encriptadas).</li>
                  <li><strong>Datos de uso:</strong> Historial de temas generados, prompts ingresados, PDFs y URLs proporcionadas para el análisis de la IA, e historial de feedback.</li>
                  <li><strong>Datos de pago:</strong> No almacenamos números de tarjetas de crédito. Todos los pagos son procesados de forma segura a través de Mercado Pago. Solo guardamos identificadores de transacciones y estados de suscripción.</li>
                </ul>

                <h3 className="text-lg font-semibold text-white mt-8 mb-3">2. Uso de la información</h3>
                <p>Utilizamos tus datos exclusivamente para:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Proporcionar y mantener el servicio operativo.</li>
                  <li>Procesar tus pagos y gestionar tus suscripciones.</li>
                  <li>Brindar soporte técnico y atención al cliente.</li>
                  <li>Mejorar nuestros algoritmos y prompts utilizando el feedback anonimizado.</li>
                </ul>

                <h3 className="text-lg font-semibold text-white mt-8 mb-3">3. Compartición con Terceros</h3>
                <p>No vendemos ni alquilamos tus datos personales. Compartimos información estrictamente necesaria con proveedores de infraestructura esenciales:</p>
                <ul className="list-disc pl-5 space-y-2">
                  <li><strong>Google (Gemini):</strong> Para generar las respuestas de IA.</li>
                  <li><strong>Mercado Pago:</strong> Para el procesamiento de pagos y suscripciones.</li>
                  <li><strong>Resend:</strong> Para el envío de correos transaccionales (verificación, recuperación de contraseña).</li>
                </ul>

                <h3 className="text-lg font-semibold text-white mt-8 mb-3">4. Derechos del Titular de los Datos</h3>
                <p>Como usuario, tienes derecho a solicitar el acceso, rectificación, actualización o eliminación de tus datos personales almacenados en nuestras bases de datos en cualquier momento. Para ejercer estos derechos, contáctanos a <strong>soporte@dinamiclassapp.com.ar</strong>.</p>

                <h3 className="text-lg font-semibold text-white mt-8 mb-3">5. Seguridad</h3>
                <p>Implementamos medidas de seguridad estándar de la industria (como encriptación JWT y contraseñas hasheadas) para proteger tus datos contra accesos no autorizados, alteraciones o destrucción.</p>
              </div>
            </div>
          )}
        </article>
      </main>
    </div>
  );
}
